
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, UserCheck, Clock, AlertCircle, RotateCcw, Settings } from 'lucide-react';
import { useUsers } from '@/hooks/useUsers';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useAuth } from '@/contexts/AuthContext';
import CreateUserModal from './CreateUserModal';
import AssignPropertiesModal from './AssignPropertiesModal';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const PropertyOwnersTab = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<any>(null);
  const { user, profile } = useAuth();
  const { data: owners = [], isLoading, error, refetch } = useUsers('property_owner');
  const { resendInvitation, loading: actionLoading } = useUserManagement();

  // Check if user is authenticated and has proper role
  if (!user || !profile || profile.role !== 'super_admin') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-yellow-800 font-medium">Acceso denegado</h3>
        <p className="text-yellow-600 text-sm mt-1">
          Solo los super administradores pueden acceder a esta sección.
        </p>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <UserCheck className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Registrado';
      case 'pending':
        return 'Pendiente';
      case 'expired':
        return 'Vencido';
      default:
        return status;
    }
  };

  const handleResendInvitation = async (userId: string) => {
    await resendInvitation(userId);
    refetch();
  };

  const handleAssignProperties = (owner: any) => {
    setSelectedOwner(owner);
    setShowAssignModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">Cargando propietarios...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Error al cargar propietarios</h3>
        <p className="text-red-600 text-sm mt-1">
          Error: {error.message}
        </p>
        <Button 
          variant="outline" 
          onClick={() => refetch()} 
          className="mt-2"
        >
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dueños de Propiedades</h2>
          <p className="text-gray-600">Gestiona los propietarios y sus propiedades asignadas</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Crear Propietario
        </Button>
      </div>

      <div className="grid gap-4">
        {Array.isArray(owners) && owners.map((owner) => (
          <Card key={owner.id}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {owner.first_name} {owner.last_name}
                      </h3>
                      <p className="text-sm text-gray-600">{owner.user_id}</p>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Teléfono:</span>
                      <span className="ml-2 text-gray-600">{owner.phone || 'No especificado'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Propiedades:</span>
                      <span className="ml-2 text-gray-600">
                        {Array.isArray(owner.user_property_assignments) ? owner.user_property_assignments.length : 0}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Creado:</span>
                      <span className="ml-2 text-gray-600">
                        {format(new Date(owner.created_at), 'dd/MM/yyyy', { locale: es })}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {getStatusIcon(owner.status)}
                      <span className="ml-2 text-gray-700">{getStatusText(owner.status)}</span>
                    </div>
                  </div>
                  
                  {Array.isArray(owner.user_property_assignments) && owner.user_property_assignments.length > 0 && (
                    <div className="mt-3">
                      <span className="font-medium text-gray-700 text-sm">Propiedades asignadas:</span>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {owner.user_property_assignments.map((assignment: any) => (
                          <span
                            key={assignment.property_id}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {assignment.properties?.short_name || assignment.property_id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAssignProperties(owner)}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Asignar
                  </Button>
                  
                  {(owner.status === 'pending' || owner.status === 'expired') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResendInvitation(owner.user_id)}
                      disabled={actionLoading}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reenviar
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!Array.isArray(owners) || owners.length === 0) && (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">No hay propietarios registrados</p>
            </CardContent>
          </Card>
        )}
      </div>

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          refetch();
        }}
        userRole="property_owner"
      />

      {selectedOwner && (
        <AssignPropertiesModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedOwner(null);
          }}
          onSuccess={() => {
            setShowAssignModal(false);
            setSelectedOwner(null);
            refetch();
          }}
          owner={selectedOwner}
        />
      )}
    </div>
  );
};

export default PropertyOwnersTab;
